/**
 * Lightweight WebSocket preconnect helper.
 *
 * Goal: start the TCP/TLS + WS handshake earlier (e.g. on input focus/typing),
 * then "hand off" the live socket to the chat modal when it mounts.
 *
 * This reduces perceived connect latency without keeping an always-on socket
 * for every visitor.
 */
type PreconnectedSocket = {
  url: string;
  ws: WebSocket;
  closeTimerId: number | null;
};

let preconnected: PreconnectedSocket | null = null;

function clearTimer(sock: PreconnectedSocket) {
  if (sock.closeTimerId !== null) {
    window.clearTimeout(sock.closeTimerId);
    sock.closeTimerId = null;
  }
}

function cleanupIfMatches(ws: WebSocket) {
  if (preconnected?.ws !== ws) return;
  clearTimer(preconnected);
  preconnected = null;
}

export function preconnectWebSocket(url: string, ttlMs: number = 90_000): void {
  if (typeof window === 'undefined') return;
  if (!url) return;

  try {
    // Reuse an existing preconnect if it's for the same URL and still viable.
    if (preconnected?.url === url) {
      const state = preconnected.ws.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      // Otherwise drop it and recreate.
      cleanupIfMatches(preconnected.ws);
    } else if (preconnected) {
      // Different URL; close old preconnect.
      try {
        preconnected.ws.close(1000, 'Preconnect replaced');
      } catch {
        // ignore
      }
      cleanupIfMatches(preconnected.ws);
    }

    const ws = new WebSocket(url);
    const sock: PreconnectedSocket = { url, ws, closeTimerId: null };

    // Auto-close if never consumed (keeps resource usage bounded).
    sock.closeTimerId = window.setTimeout(() => {
      try {
        if (sock.ws.readyState === WebSocket.OPEN || sock.ws.readyState === WebSocket.CONNECTING) {
          sock.ws.close(1000, 'Preconnect TTL elapsed');
        }
      } catch {
        // ignore
      } finally {
        cleanupIfMatches(sock.ws);
      }
    }, ttlMs);

    // If it dies before being consumed, forget it.
    ws.addEventListener('close', () => cleanupIfMatches(ws), { once: true });
    ws.addEventListener('error', () => cleanupIfMatches(ws), { once: true });

    preconnected = sock;
  } catch {
    // ignore
  }
}

/**
 * Take ownership of a preconnected socket (if any).
 * Returns null when no viable preconnect exists.
 */
export function takePreconnectedWebSocket(url: string): WebSocket | null {
  if (typeof window === 'undefined') return null;
  if (!url) return null;

  try {
    if (!preconnected || preconnected.url !== url) return null;
    const { ws } = preconnected;
    const state = ws.readyState;

    if (state !== WebSocket.OPEN && state !== WebSocket.CONNECTING) {
      cleanupIfMatches(ws);
      return null;
    }

    clearTimer(preconnected);
    preconnected = null;
    return ws;
  } catch {
    return null;
  }
}

